import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  bitmaskToStrings,
  stringsToBitmask,
  checkPermission,
  hasWildcard as hasWildcardPerm,
} from '../../utils/permissionUtils';
import MultiSelect, { type MultiSelectOption } from '../../components/MultiSelect';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import { formatEventOption, sortEventsByDate } from '../../utils/eventUtils';
import '../../App.css';
import './RolesPage.css';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { ConfirmModal } from '../../components/ui';
import { translateError, errorCode } from '../../utils/errorMessages';
import { membersOnlyFromRemovedGroups } from '../../utils/groupMembers';

interface IRole {
  _id: string;
  name: string;
  description?: string;
  permissions: Record<string, number>;
  isSystem: boolean;
  type: 'admin' | 'user';
  limitDataAccess?: boolean;
  accessGrants?: {
    activities: number[];
    groups: number[];
    members: number[];
    events: number[];
    eventTemplates: number[];
  };
}

interface PermissionsData {
  all: string[];
  grouped: Record<string, string[]>;
}

const PERMISSION_CATEGORY_ORDER = [
  'members', 'groups', 'events', 'templates', 'attendance',
  'activities', 'memberships',
  // 'organization', not 'org': the category key is the RESOURCE name, and the
  // old 'org' entry never matched anything, so Organization sorted last and
  // rendered with its raw key as the heading.
  'payments', 'organization', 'users', 'roles', 'statistics', 'requirements',
  'announcements',
];

// User-type custom roles can only toggle these permissions
const USER_TYPE_TOGGLEABLE = ['members:write', 'members:delete'];

const EMPTY_GRANTS: NonNullable<IRole['accessGrants']> = { activities: [], groups: [], members: [], events: [], eventTemplates: [] };

const RoleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { currentMembership } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [role, setRole] = useState<IRole | null>(null);
  const [permissionsData, setPermissionsData] = useState<PermissionsData | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [limitDataAccess, setLimitDataAccess] = useState(false);
  const [accessGrants, setAccessGrants] = useState<{
    activities: number[];
    groups: number[];
    members: number[];
    events: number[];
    eventTemplates: number[];
  }>({ activities: [], groups: [], members: [], events: [], eventTemplates: [] });
  const [advancedGrantsOpen, setAdvancedGrantsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grantScopeError, setGrantScopeError] = useState(false);
  // Members that only came from a just-removed group label (asked before dropping).
  const [labelMembersPrompt, setLabelMembersPrompt] = useState<number[] | null>(null);

  // Entity options for access grants pickers
  const [activityOptions, setActivityOptions] = useState<MultiSelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<MultiSelectOption[]>([]);
  const [memberOptions, setMemberOptions] = useState<MultiSelectOption[]>([]);
  const [eventOptions, setEventOptions] = useState<MultiSelectOption[]>([]);
  const [templateOptions, setTemplateOptions] = useState<MultiSelectOption[]>([]);

  const myPerms = (currentMembership?.role as IRole | undefined)?.permissions;
  const myHasWildcard = hasWildcardPerm(myPerms);
  // A data-limited editor may only ever produce data-limited roles.
  const iAmScoped = Boolean(currentMembership?.role?.limitDataAccess) && !myHasWildcard;

  // Server copy is authoritative: saving expands newly applied groups into members.
  const seedFromRole = useCallback((roleData: IRole) => {
    setRole(roleData);
    setEditName(roleData.name);
    setEditDescription(roleData.description || '');
    setSelectedPermissions(new Set(bitmaskToStrings(roleData.permissions)));
    setLimitDataAccess(roleData.limitDataAccess || false);
    setAccessGrants(roleData.accessGrants || EMPTY_GRANTS);
  }, []);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [roleRes, permsRes] = await Promise.all([
        api.roles.get(id),
        api.roles.getPermissions(),
      ]);
      const roleData = roleRes.data as IRole;
      seedFromRole(roleData);
      setPermissionsData(permsRes.data as PermissionsData);
      setError(null);

      // Load entity options for access grant pickers (only for non-system roles)
      if (!roleData.isSystem) {
        const [activitiesRes, groupsRes, membersRes, eventsRes, templatesRes] = await Promise.all([
          api.activities.list(),
          api.groups.list(),
          api.members.list(),
          api.events.list(),
          api.eventTemplates.list(),
        ]);
        setActivityOptions((activitiesRes.data as any[]).map((a: any) => ({ _id: a._id, name: a.name })));
        setGroupOptions((groupsRes.data as any[]).map((g: any) => ({ _id: g._id, name: g.name })));
        setMemberOptions((membersRes.data as any[]).map((m: any) => ({ _id: m._id, name: m.name })));
        const sortedEvents = sortEventsByDate(eventsRes.data as any[]);
        setEventOptions(sortedEvents.map((e: any) => ({ _id: e._id, name: formatEventOption(e, i18n.language) })));
        setTemplateOptions((templatesRes.data as any[]).map((t: any) => ({ _id: t._id, name: t.name })));
      }
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, seedFromRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isSystemRole = role?.isSystem || false;

  const canEditPermission = useCallback((permission: string): boolean => {
    if (!role) return false;

    // System roles: all permissions are read-only
    if (isSystemRole) return false;

    // Custom user-type roles: only members:write and members:delete toggleable
    if (role.type === 'user') {
      return USER_TYPE_TOGGLEABLE.includes(permission);
    }

    // Custom admin-type roles: can toggle any permission the editor's own role has
    if (myHasWildcard) return true;
    return myPerms ? checkPermission(myPerms, permission) : false;
  }, [role, isSystemRole, myHasWildcard, myPerms]);

  const rolePermissionStrings = useMemo(
    () => (role ? new Set(bitmaskToStrings(role.permissions)) : new Set<string>()),
    [role],
  );

  const hasChanges = useMemo(() => {
    if (!role) return false;
    // Check permissions changes
    if (rolePermissionStrings.size !== selectedPermissions.size) return true;
    if (Array.from(selectedPermissions).some(p => !rolePermissionStrings.has(p))) return true;
    // Check name/description changes
    if (editName !== role.name) return true;
    if (editDescription !== (role.description || '')) return true;
    // Check limitDataAccess changes
    if (limitDataAccess !== (role.limitDataAccess || false)) return true;
    // Check accessGrants changes
    const origGrants = role.accessGrants || EMPTY_GRANTS;
    const sortArr = (a: number[]) => [...a].sort((x, y) => x - y);
    const sortGrants = (g: typeof origGrants) => ({
      activities: sortArr(g.activities || []),
      groups: sortArr(g.groups), members: sortArr(g.members),
      events: sortArr(g.events), eventTemplates: sortArr(g.eventTemplates),
    });
    if (JSON.stringify(sortGrants(accessGrants)) !== JSON.stringify(sortGrants(origGrants))) return true;
    return false;
  }, [role, rolePermissionStrings, selectedPermissions, editName, editDescription, limitDataAccess, accessGrants]);

  /** Removing a group label offers to drop the members it brought (default keep). */
  const handleGroupsChange = async (ids: (string | number)[]) => {
    const next = ids.map(Number);
    const removed = accessGrants.groups.filter(g => !next.includes(Number(g)));
    setAccessGrants(prev => ({ ...prev, groups: next }));
    if (removed.length === 0) return;
    try {
      const candidates = await membersOnlyFromRemovedGroups(removed, next, accessGrants.members);
      if (candidates.length > 0) setLabelMembersPrompt(candidates);
    } catch {
      // Lookup failed: keep the members, which is the default answer anyway.
    }
  };

  const handleDropLabelMembers = () => {
    const drop = new Set(labelMembersPrompt ?? []);
    setAccessGrants(prev => ({ ...prev, members: prev.members.filter(m => !drop.has(Number(m))) }));
    setLabelMembersPrompt(null);
  };

  const handleToggle = (permission: string) => {
    if (!canEditPermission(permission)) return;

    setSelectedPermissions(prev => {
      const next = new Set(prev);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!role || !hasChanges) return;

    if (selectedPermissions.size === 0) {
      addToast(t('roles.atLeastOnePermission'), 'warning');
      return;
    }

    setSaving(true);
    setGrantScopeError(false);
    try {
      const permsBitmask = stringsToBitmask(Array.from(selectedPermissions));
      const res = await api.roles.update(role._id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        permissions: permsBitmask,
        type: role.type,
        limitDataAccess: iAmScoped ? true : limitDataAccess,
        accessGrants: limitDataAccess ? accessGrants : undefined,
      });
      seedFromRole(res.data as IRole);
      addToast(t('roles.updateSuccess'), 'success');
    } catch (err: any) {
      if (errorCode(err) === 'ROLE_GRANT_OUTSIDE_SCOPE') {
        setGrantScopeError(true);
        addToast(t('roles.grantOutsideScope'), 'error');
      } else {
        addToast(translateError(err, t, 'roles.updateError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!role) return;

    setDeleting(true);
    try {
      await api.roles.delete(role._id);
      addToast(t('roles.deleteSuccess'), 'success');
      navigate('/admin/roles');
    } catch (err: any) {
      addToast(translateError(err, t, 'roles.deleteError'), 'error');
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReset = () => {
    if (!role) return;
    seedFromRole(role);
    setGrantScopeError(false);
  };

  const sortedCategories = useMemo(() => {
    if (!permissionsData?.grouped) return [];
    const categories = Object.keys(permissionsData.grouped);
    return categories.sort((a, b) => {
      const ai = PERMISSION_CATEGORY_ORDER.indexOf(a);
      const bi = PERMISSION_CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [permissionsData]);

  if (loading) {
    return (
      <div className="admin-list-page">
        <SkeletonTable rows={5} columns={3} />
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="admin-list-page">
        <p className="error-message">{error || t('roles.notFound')}</p>
        <Link to="/admin/roles">&larr; {t('common.back')}</Link>
      </div>
    );
  }

  const isWildcardRole = hasWildcardPerm(role.permissions);

  return (
    <div className="admin-list-page">
      <div className="role-detail-back">
        <Link to="/admin/roles" className="back-link">&larr; {t('common.back')}</Link>
      </div>

      {/* Header: editable name for custom roles, static for system */}
      {isSystemRole ? (
        <>
          <div className="role-detail-header">
            <h2>{role.name}</h2>
            <span className={`type-badge type-badge--${role.type}`}>
              {t(`roles.types.${role.type}`)}
            </span>
            <span className="system-badge">{t('roles.system')}</span>
          </div>
          {role.description && (
            <p className="page-description">{role.description}</p>
          )}
        </>
      ) : (
        <>
          <div className="role-detail-header">
            <h2>{role.name}</h2>
            <span className={`type-badge type-badge--${role.type}`}>
              {t(`roles.types.${role.type}`)}
            </span>
          </div>
          <div className="role-form-narrow">
            <div className="role-edit-fields">
              <FormField htmlFor="edit-name" label={t('roles.nameLabel')}>
                <input
                  id="edit-name"
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  maxLength={50}
                />
              </FormField>
              <FormField htmlFor="edit-description" label={t('roles.descriptionLabel')}>
                <textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  maxLength={255}
                  rows={2}
                />
              </FormField>
            </div>
          </div>
        </>
      )}

      {/* Info banners */}
      {isSystemRole && (
        <div className="role-info-banner role-info-banner--info">
          <i className="fas fa-info-circle"></i>
          {t('roles.systemReadOnly')}
        </div>
      )}

      {!isSystemRole && role.type === 'user' && (
        <div className="role-info-banner role-info-banner--warning">
          <i className="fas fa-exclamation-triangle"></i>
          {t('roles.userTypeRestriction')}
        </div>
      )}

      {/* Permissions grid */}
      {isWildcardRole ? (
        <div className="role-wildcard-notice">
          <i className="fas fa-shield-alt"></i>
          {t('roles.allPermissions')}
        </div>
      ) : (
        <div className="permissions-grid">
          {sortedCategories.map(category => {
            const perms = permissionsData!.grouped[category]!;
            return (
              <div key={category} className="permission-category">
                <h4>
                  {t(`roles.permissionCategories.${category}`, category)}
                </h4>
                {perms.map(perm => {
                  const isChecked = selectedPermissions.has(perm);
                  const isEditable = canEditPermission(perm);
                  const action = perm.split(':')[1] || perm;
                  return (
                    <div
                      key={perm}
                      className={`permission-label ${isEditable ? 'permission-label--editable' : 'permission-label--readonly'}`}
                    >
                      <ToggleSwitch
                        checked={isChecked}
                        disabled={!isEditable}
                        onChange={() => handleToggle(perm)}
                        label={<span>{t(`roles.actions.${action}`, action)}</span>}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Limit Data Access */}
      {!isSystemRole && (
        <div className="form-group" style={{ marginTop: '1.5rem' }}>
          <ToggleSwitch
            checked={limitDataAccess}
            onChange={setLimitDataAccess}
            // You cannot grant access you do not hold, and that includes ROW
            // scope: a data-limited editor turning this off would produce a
            // role with org-wide reach it does not have itself. The backend
            // refuses it either way; this keeps the UI honest about it.
            disabled={iAmScoped}
            label={
              <div>
                <strong>{t('roles.limitDataAccess')}</strong>
                <p className="form-help-text">
                  {iAmScoped
                    ? t('roles.limitDataAccessLockedToScope')
                    : t('roles.limitDataAccessDescription')}
                </p>
              </div>
            }
          />
        </div>
      )}

      {/* Access Grants */}
      {!isSystemRole && limitDataAccess && (
        <div className="form-group access-grants-section">
          <label>{t('roles.accessGrants')}</label>
          {/* Primary scope: Activities */}
          <div className="access-grants-grid">
            <MultiSelect
              label={t('roles.activityAccess')}
              options={activityOptions}
              selectedIds={accessGrants.activities}
              onChange={(ids) => setAccessGrants(prev => ({ ...prev, activities: ids.map(Number) }))}
              placeholder={t('roles.activityAccess')}
            />
          </div>
          <small className="form-help-text">{t('roles.activityAccessDescription')}</small>

          {/* Advanced: per-entity grants (overrides) */}
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="collapsible-toggle"
              onClick={() => setAdvancedGrantsOpen(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit', color: 'inherit' }}
            >
              {advancedGrantsOpen ? '▾' : '▸'} {t('roles.advancedPerEntityGrants')}
            </button>
            {advancedGrantsOpen && (
              <div className="access-grants-grid" style={{ marginTop: '0.5rem' }}>
                <MultiSelect
                  label={t('roles.accessGroups')}
                  options={groupOptions}
                  selectedIds={accessGrants.groups}
                  onChange={(ids) => { void handleGroupsChange(ids); }}
                  placeholder={t('roles.accessGroups')}
                />
                <MultiSelect
                  label={t('roles.accessMembers')}
                  options={memberOptions}
                  selectedIds={accessGrants.members}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, members: ids.map(Number) }))}
                  placeholder={t('roles.accessMembers')}
                />
                <MultiSelect
                  label={t('roles.accessEventTemplates')}
                  options={templateOptions}
                  selectedIds={accessGrants.eventTemplates}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, eventTemplates: ids.map(Number) }))}
                  placeholder={t('roles.accessEventTemplates')}
                />
                <MultiSelect
                  label={t('roles.accessEvents')}
                  options={eventOptions}
                  selectedIds={accessGrants.events}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, events: ids.map(Number) }))}
                  placeholder={t('roles.accessEvents')}
                />
              </div>
            )}
            {advancedGrantsOpen && (
              <small className="form-help-text">{t('roles.accessGroupsDescription')}</small>
            )}
          </div>
        </div>
      )}

      {grantScopeError && (
        <div className="role-info-banner role-info-banner--warning" role="alert">
          <i className="fas fa-exclamation-triangle"></i>
          {t('roles.grantOutsideScope')}
        </div>
      )}

      {/* Save / Cancel / Delete buttons */}
      {!isSystemRole && (
        <div className="role-detail-actions">
          <Button
            onClick={handleSave}
            disabled={!hasChanges}
            loading={saving}
          >
            {t('common.save')}
          </Button>
          {hasChanges && (
            <Button variant="secondary" onClick={handleReset}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => setDeleteConfirmOpen(true)}
            loading={deleting}
          >
            {t('roles.deleteRole')}
          </Button>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirmTitle')}
        message={t('roles.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        isOpen={labelMembersPrompt !== null}
        onClose={() => setLabelMembersPrompt(null)}
        onConfirm={handleDropLabelMembers}
        title={t('roles.removeGroupMembersTitle')}
        message={t('roles.removeGroupMembersMessage', {
          names: (labelMembersPrompt ?? [])
            .map(mId => memberOptions.find(o => Number(o._id) === mId)?.name ?? `#${mId}`)
            .join(', '),
        })}
        confirmText={t('roles.removeGroupMembersConfirm')}
        cancelText={t('roles.removeGroupMembersKeep')}
      />
    </div>
  );
};

export default RoleDetailPage;
